import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, Box, AlertTriangle } from "lucide-react";
import { API, setSession } from "../../lib/auth";

export default function TenantLogin() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  // workspace lookup state
  const [lookup, setLookup] = useState({ status: "checking", workspace: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    setLookup({ status: "checking", workspace: null, error: "" });
    axios
      .get(`${API}/tenants/lookup/${subdomain}`)
      .then((r) => {
        if (cancelled) return;
        setLookup({ status: "ok", workspace: r.data, error: "" });
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err.response?.status;
        const detail = err.response?.data?.detail;
        if (code === 404) setLookup({ status: "not_found", workspace: null, error: detail || "Workspace not found" });
        else setLookup({ status: "error", workspace: null, error: detail || "Could not validate workspace" });
      });
    return () => { cancelled = true; };
  }, [subdomain]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (lookup.status !== "ok") return;
    if (lookup.workspace?.status && lookup.workspace.status !== "active") {
      return toast.error(`Workspace is ${lookup.workspace.status}. Please contact support.`);
    }
    if (!form.email.includes("@") || form.password.length < 6) return toast.error("Enter valid credentials");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { ...form, subdomain });
      setSession({ token: data.token, user: data.user, tenant: data.tenant });
      toast.success(`Welcome back to ${data.tenant.company_name}`);
      nav(`/t/${subdomain}/dashboard`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  // ───── render: validation phase ─────
  if (lookup.status === "checking") {
    return (
      <div data-testid="tenant-login-checking" className="min-h-screen bg-[#FFFCF2] flex items-center justify-center">
        <div className="flex items-center gap-3 text-black/55 text-sm">
          <span className="w-5 h-5 rounded-full border-2 border-[#1FA868]/20 border-t-[#1FA868] animate-spin" />
          Validating workspace <span className="font-mono font-bold text-black">{subdomain}</span>…
        </div>
      </div>
    );
  }

  if (lookup.status === "not_found" || lookup.status === "error") {
    return (
      <div data-testid="tenant-login-not-found" className="min-h-screen bg-[#FFFCF2] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-black/8 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 mx-auto flex items-center justify-center"><AlertTriangle size={26} /></div>
          <h1 className="font-display font-extrabold text-2xl text-black mt-5">Workspace not found</h1>
          <p className="text-sm text-black/60 mt-2">
            We couldn't find a workspace with the URL{" "}
            <span className="font-mono font-bold text-black">{subdomain}.brivo360.com</span>.
            Double-check the spelling or create a new workspace.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <Link to="/register" data-testid="not-found-register"
              className="block w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold">
              Create a new workspace
            </Link>
            <Link to="/" className="block text-sm text-black/55 hover:text-black mt-1">← Back to brivo360.com</Link>
          </div>
        </div>
      </div>
    );
  }

  const ws = lookup.workspace;
  const suspended = ws?.status && ws.status !== "active";

  return (
    <div data-testid="tenant-login-page" className="min-h-screen bg-[#FFFCF2] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden bg-gradient-to-br from-[#1B4332] via-[#2D6A4F] to-[#52B788] text-white p-12 flex-col justify-between">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <Link to="/" className="relative inline-flex items-center gap-2 text-white/80 hover:text-white text-sm">
          <ArrowLeft size={16} /> Back to brivo360.com
        </Link>
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-7"><Box size={32} /></div>
          <h1 className="font-display text-5xl font-extrabold tracking-tight leading-[1.05]">Welcome back to your workspace.</h1>
          <p className="mt-5 text-white/80 text-lg max-w-md">Log into <span className="font-semibold">{subdomain}.brivo360.com</span> to manage your business.</p>
        </div>
        <div className="text-xs text-white/50">© Brivo360. 2026</div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6">
          <img 
            src="https://brivo360.com/images/brivo-logo2.png" 
            alt="Brivo360" 
            className="h-10 w-auto cursor-pointer"
          />
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-black tracking-tight">Log in</h2>
          <div className="mt-2 flex items-center gap-2">
            {ws?.logo_url && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${ws.logo_url}`} alt="" className="h-6 w-6 rounded object-contain" />
            )}
            <p className="text-black/60">
              Workspace: <span className="font-semibold text-black">{ws?.company_name || subdomain}</span>{" "}
              <span className="font-mono text-black/45 text-sm">({subdomain})</span>
            </p>
          </div>
          {suspended && (
            <div data-testid="ws-suspended-banner"
              className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center gap-2">
              <AlertTriangle size={14} /> Workspace status is <strong className="capitalize">{ws.status}</strong>. Contact support to restore access.
            </div>
          )}
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Email</label>
              <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                <Mail size={16} className="text-black/40" />
                <input data-testid="tenant-login-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="flex-1 px-3 py-3 focus:outline-none" placeholder="you@business.com" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Password</label>
              <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                <Lock size={16} className="text-black/40" />
                <input data-testid="tenant-login-password" type={show ? "text" : "password"} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-3 py-3 focus:outline-none" placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)} className="text-black/40">
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <button data-testid="tenant-login-submit" type="submit" disabled={loading || suspended}
              className="w-full mt-2 py-3.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Logging in..." : "Log in"} <ArrowRight size={16} />
            </button>
          </form>
          <p className="mt-5 text-sm text-center text-black/60">
            Need a workspace? <Link to="/register" className="text-[#1B4332] font-semibold">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
